import { IntercomUserData } from "@faro-lotv/foreign-observers";
import { IElementsState } from "@faro-lotv/project-source";
import {
  ArchivalStatus,
  CompanyRole,
  ProjectPermissions,
  RegisteredUserInfo,
} from "@faro-lotv/service-wires";
import { UserState } from "./user-slice";

type State = { user: UserState };

/**
 * @returns the current logged in user info if available or undefined if there's no logged in user
 * @param state app state
 */
export function selectCurrentUser({
  user,
}: State): RegisteredUserInfo | undefined {
  return user.user;
}

type OnlyIElementsAndUserState = {
  /** The user state slice of the app store */
  user: UserState;
  /** The iElements state slice of the app store */
  iElements: IElementsState;
};

/**
 * @returns true if the user is logged in and has write permission, and if the project is not archived
 * @param state app state
 */
export function selectHasWritePermission({
  user,
  iElements,
}: OnlyIElementsAndUserState): boolean {
  if (iElements.projectArchivalState === ArchivalStatus.Archived) return false;

  if (!user.user?.projectPermissions) return false;
  return user.user.projectPermissions.includes(ProjectPermissions.write);
}

/**
 * @returns true if the user is logged in and has admin permission, and if the project is not archived
 * @param state app state
 */
export function selectHasAdminPermission({
  user,
  iElements,
}: OnlyIElementsAndUserState): boolean {
  if (iElements.projectArchivalState === ArchivalStatus.Archived) return false;

  if (!user.user?.projectPermissions) return false;
  return user.user.projectPermissions.includes(ProjectPermissions.admin);
}

/**
 * @returns true if the user is logged in and has a role in the project.
 * This is not the case, when the project is accessed as an unlisted user.
 * @param state app state
 */
export function selectHasUserProjectRole({ user }: State): boolean {
  return !!user.projectRole;
}

/**
 * @returns True if the current user is allowed to buy tokens in the Dashboard
 * @param state Current app state
 */
export function selectIsAllowedToBuyTokens({ user }: State): boolean {
  return user.companyRole === CompanyRole.companyAdmin;
}

/**
 * @returns the current user's intercom info, if available
 * @param state app state
 */
export function selectIntercomInfo({
  user,
}: State): IntercomUserData | undefined {
  return user.intercomInfo;
}

/**
 * @returns the user slice of the app state
 */
export function selectUserSlice({ user }: State): UserState {
  return user;
}
